/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/AppointmentRequestSections/Location/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../../utils/logger.js";
import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "./Location.module.css";
import { useTranslation } from "react-i18next";
import { getReferenceData } from "../../../logic/companyEdit";
import { useNavigate } from "react-router";
import Select from "react-select";
import {
  getReferencedDataOptionById,
  selectStyles,
} from "../../../utils/common";

const Location = ({ company, getFieldValidation }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isTablet = window.innerWidth <= 1023;
  const isMediumMobile = window.innerWidth <= 767;
  const isSmallMobile = window.innerWidth <= 480;
  const isNotComputer = isTablet || isMediumMobile || isSmallMobile;

  const getNotSingleSelectWidth = () => {
    return "100%";
  };

  const secondAddressCountryRef = useRef(null);
  const secondAddressStateRef = useRef(null);
  const secondAddressTownRef = useRef(null);

  const [secondAddressCountries, setSecondAddressCountries] = useState([]);
  const [secondAddressStates, setSecondAddressStates] = useState([]);
  const [secondAddressTowns, setSecondAddressTowns] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReferenceData = useCallback(async () => {
    try {
      logger.info("company: ", { data: company });
      logger.info("Fetching reference data...");
      setIsLoading(true);
      setError(null);

      const [countriesData, statesData, townsData] = await Promise.all([
        getReferenceData("countries", navigate),
        getReferenceData("states", navigate),
        getReferenceData("towns", navigate),
      ]);

      logger.info("Reference data fetched successfully");
      logger.info("Countries:", { data: countriesData });
      logger.info("States:", { data: statesData });
      logger.info("Towns:", { data: townsData });

      setSecondAddressCountries(
        countriesData && countriesData.length > 0 ? countriesData : [],
      );
      setSecondAddressStates(
        statesData && statesData.length > 0 ? statesData : [],
      );
      setSecondAddressTowns(townsData && townsData.length > 0 ? townsData : []);
    } catch (err) {
      logger.error("Error fetching reference data:", {
        err: sanitizeError(err),
      });
      setError(t("errors.loading.referenceDataFailed"));
      setSecondAddressCountries([]);
      setSecondAddressStates([]);
      setSecondAddressTowns([]);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const getOptionValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }
    return value;
  };

  const getSafeSelectedOption = (options, value) => {
    if (!Array.isArray(options)) {
      logger.warn("Options is not an array:", { data: options });
      return null;
    }
    return getReferencedDataOptionById(options, getOptionValue(value));
  };

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  return (
    <div className={styles.sectionContent}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t("components.location.address2Title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>
      <div className={`${styles.inputRow} ${styles.twoInputsRow}`}>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.location.countryLabel")}
          </label>
          <Select
            ref={secondAddressCountryRef}
            isSearchable={true}
            value={getSafeSelectedOption(
              secondAddressCountries,
              company?.domicilio2Pais || "",
            )}
            isDisabled={true}
            options={secondAddressCountries}
            placeholder={t("components.location.countryPlaceholder")}
            styles={selectStyles}
            className="select__control"
            getOptionValue={(option) => option._id}
            formatOptionLabel={(option) => <span>{option.nombre}</span>}
          />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.location.stateLabel")}
          </label>
          <Select
            ref={secondAddressStateRef}
            isSearchable={true}
            value={getSafeSelectedOption(
              secondAddressStates,
              company?.domicilio2Estado || "",
            )}
            isDisabled={true}
            options={secondAddressStates}
            placeholder={t("components.location.statePlaceholder")}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderRadius: "8px",
                fontSize: "14px",
                cursor: "pointer",
                borderColor:
                  getFieldValidation && getFieldValidation("domicilio2Pais")
                    ? "#dc3545"
                    : baseStyles.borderColor,
                marginBottom: "-10px",
                width: getNotSingleSelectWidth(),
              }),
              container: (baseStyles) => ({
                ...baseStyles,
              }),
              valueContainer: (baseStyles) => ({
                ...baseStyles,
              }),
              input: (baseStyles) => ({
                ...baseStyles,
              }),
              option: (baseStyles) => ({
                ...baseStyles,
                cursor: "pointer",
              }),
              indicatorSeparator: () => ({ display: "none" }),
            }}
            className="select__control"
            getOptionValue={(option) => option._id}
            formatOptionLabel={(option) => <span>{option.nombre}</span>}
          />
        </div>
        <div className={`${styles.inputGroup} ${styles.twoLabelsGroup}`}>
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>
              {t("components.location.townLabel")}
            </label>
            <Select
              ref={secondAddressTownRef}
              isSearchable={true}
              value={getSafeSelectedOption(
                secondAddressTowns,
                company?.domicilio2Municipio || "",
              )}
              isDisabled={true}
              options={secondAddressTowns}
              placeholder={t("components.location.townPlaceholder")}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderRadius: "8px",
                  fontSize: "14px",
                  cursor: "pointer",
                  borderColor:
                    getFieldValidation && getFieldValidation("domicilio2Estado")
                      ? "#dc3545"
                      : baseStyles.borderColor,
                  marginBottom: "-10px",
                  width: getNotSingleSelectWidth(),
                }),
                container: (baseStyles) => ({
                  ...baseStyles,
                }),
                valueContainer: (baseStyles) => ({
                  ...baseStyles,
                }),
                input: (baseStyles) => ({
                  ...baseStyles,
                }),
                option: (baseStyles) => ({
                  ...baseStyles,
                  cursor: "pointer",
                }),
                indicatorSeparator: () => ({ display: "none" }),
              }}
              className="select__control"
              getOptionValue={(option) => option._id}
              formatOptionLabel={(option) => <span>{option.nombre}</span>}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Location;
